import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Sparkles,
  ArrowLeft,
  Home,
  Settings,
  Bell,
  Users,
  Award,
  Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface ActivationStepProps {
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  canGoBack: boolean;
  isRequired: boolean;
  estimatedTime: string;
}

interface WelcomeFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  available: boolean;
}

export const ActivationStep: React.FC<ActivationStepProps> = ({
  onComplete,
  onSkip,
  onPrevious,
  canGoBack,
  isRequired,
  estimatedTime
}) => {
  const navigate = useNavigate();
  const [activating, setActivating] = useState(false);

  const welcomeFeatures: WelcomeFeature[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      description: 'Your personalized overview of recalls, campaigns, and assignments',
      icon: <Home className='h-5 w-5' />,
      available: true
    },
    {
      id: 'recalls',
      title: 'Recall Management',
      description: 'View and manage vehicle recalls and safety campaigns',
      icon: <Award className='h-5 w-5' />,
      available: true
    },
    {
      id: 'assignments',
      title: 'Technician Assignments',
      description: 'View your assigned tasks and update your status',
      icon: <Users className='h-5 w-5' />,
      available: true
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Stay updated with important alerts and updates',
      icon: <Bell className='h-5 w-5' />,
      available: true
    },
    {
      id: 'profile',
      title: 'Profile Settings',
      description: 'Manage your account information and preferences',
      icon: <Settings className='h-5 w-5' />,
      available: true
    },
    {
      id: 'calendar',
      title: 'Schedule',
      description: 'View your work schedule and upcoming appointments',
      icon: <Calendar className='h-5 w-5' />,
      available: false
    }
  ];

  const handleActivate = async () => {
    try {
      setActivating(true);

      // In a real implementation, this would activate the user account
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      toast.success('Account activated successfully!');
      onComplete();
    } catch (error) {
      toast.error('Failed to activate account');
    } finally {
      setActivating(false);
    }
  };

  const handleExploreFeature = (featureId: string) => {
    const routes: Record<string, string> = {
      dashboard: '/',
      recalls: '/recalls',
      assignments: '/technicians',
      notifications: '/profile/notifications',
      profile: '/profile',
      calendar: '/schedule'
    };

    const route = routes[featureId];
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center space-y-4'>
        <div className='flex items-center justify-center gap-2'>
          <Sparkles className='h-8 w-8 text-primary' />
          <h3 className='text-2xl font-semibold'>Welcome to Vehix!</h3>
        </div>
        <p className='text-muted-foreground max-w-2xl mx-auto'>
          Your account has been successfully set up and approved. You're now ready to start using
          the platform!
        </p>
        <Badge variant='outline'>{estimatedTime}</Badge>
      </div>

      {/* Activation Status */}
      <Card className='border-green-200 bg-green-50/50'>
        <CardContent className='p-6 text-center'>
          <div className='space-y-4'>
            <div className='mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center'>
              <CheckCircle className='h-8 w-8 text-green-600' />
            </div>
            <div>
              <h4 className='text-lg font-semibold text-green-900'>Account Ready for Activation</h4>
              <p className='text-green-700'>
                All setup steps have been completed successfully. Click the button below to activate
                your account.
              </p>
            </div>
            <Button
              size='lg'
              onClick={handleActivate}
              disabled={activating}
              className='bg-green-600 hover:bg-green-700'
            >
              {activating ? (
                <>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                  Activating...
                </>
              ) : (
                <>
                  <Sparkles className='h-4 w-4 mr-2' />
                  Activate My Account
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Platform Features */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Award className='h-5 w-5' />
            Platform Features
          </CardTitle>
          <CardDescription>Explore the key features available to you</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {welcomeFeatures.map(feature => (
              <div
                key={feature.id}
                className={`p-4 rounded-lg border transition-colors ${
                  feature.available
                    ? 'border-border hover:border-primary/50 cursor-pointer'
                    : 'border-muted bg-muted/50 opacity-60'
                }`}
                onClick={() => feature.available && handleExploreFeature(feature.id)}
              >
                <div className='flex items-start gap-3'>
                  <div
                    className={`flex-shrink-0 ${
                      feature.available ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {feature.icon}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <h4 className='font-medium'>{feature.title}</h4>
                      {feature.available ? (
                        <Badge variant='default' className='text-xs'>
                          Available
                        </Badge>
                      ) : (
                        <Badge variant='outline' className='text-xs'>
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <p className='text-sm text-muted-foreground mt-1'>{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Start Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Start Guide</CardTitle>
          <CardDescription>Here's what you can do to get started</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='space-y-3'>
            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium'>
                1
              </div>
              <div>
                <h4 className='font-medium'>Complete Your Profile</h4>
                <p className='text-sm text-muted-foreground'>
                  Add your certifications, specializations, and emergency contact information.
                </p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium'>
                2
              </div>
              <div>
                <h4 className='font-medium'>Review Your Assignments</h4>
                <p className='text-sm text-muted-foreground'>
                  Check your dashboard for any assigned recalls or campaigns.
                </p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium'>
                3
              </div>
              <div>
                <h4 className='font-medium'>Set Up Notifications</h4>
                <p className='text-sm text-muted-foreground'>
                  Configure your notification preferences to stay updated.
                </p>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium'>
                4
              </div>
              <div>
                <h4 className='font-medium'>Start Working</h4>
                <p className='text-sm text-muted-foreground'>
                  Begin managing recalls and updating your task status.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support Information */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
          <CardDescription>We're here to help you get started</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='p-4 bg-muted/50 rounded-lg'>
              <h4 className='font-medium mb-2'>Documentation</h4>
              <p className='text-sm text-muted-foreground mb-3'>
                Access our comprehensive user guides and tutorials.
              </p>
              <Button variant='outline' size='sm'>
                View Documentation
              </Button>
            </div>
            <div className='p-4 bg-muted/50 rounded-lg'>
              <h4 className='font-medium mb-2'>Contact Support</h4>
              <p className='text-sm text-muted-foreground mb-3'>
                Get help from our support team when you need it.
              </p>
              <Button variant='outline' size='sm'>
                Contact Support
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6 border-t'>
        <div className='flex gap-2'>
          {canGoBack && (
            <Button variant='outline' onClick={onPrevious}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>
          )}
        </div>

        <div className='flex gap-2'>
          {!isRequired && (
            <Button variant='ghost' onClick={onSkip}>
              Skip for now
            </Button>
          )}
          <Button onClick={handleActivate} disabled={activating} size='lg'>
            {activating ? (
              <>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                Activating...
              </>
            ) : (
              <>
                <Sparkles className='h-4 w-4 mr-2' />
                Activate Account
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
